import {
  EmailFolderInfo,
  MailConfigType,
  MailFolderDto,
  MailgunDeliveryFailureReportDTO,
  MailgunDomainDTO,
  MailOutBoundConfigDTO,
  MessagesFilterIMAP,
  PageWrapper,
} from '../Types/MailT';
import { IdPersonDescriptionEmailDto, MessageDTO } from '../Types/MessageT';
import { ajaxActions } from './AjaxActions';
import { BrowserS } from './BrowserS';
import { ConstantS } from './ConstantS';
import { DataS } from './DataS';
import { MixpanelS } from './MixpanelS';
import { ToastS } from './ToastS';
import { isDefined } from './ValidationS';

const BASE_URL = process.env.REACT_APP_MAIL_SERVICE_URL;

const create = async (body: Partial<MailOutBoundConfigDTO>): Promise<MailOutBoundConfigDTO | null> => {
  const res = await ajaxActions.post(`${BASE_URL}/mailOutboundConfigs`, body);
  if (res.status === 201) {
    ToastS.info('outbound-config-created', 'Mail-Konfiguration erstellt.');
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const fetchMailOutboundConfig = async (id: number): Promise<MailOutBoundConfigDTO | null> => {
  const res = await ajaxActions.get(`${BASE_URL}/mailOutboundConfigs/${id}`);
  if (res.ok) {
    return res.json();
  }
  return null;
};

const fetchMailOutboundConfigs = async (): Promise<MailOutBoundConfigDTO[]> => {
  const res = await ajaxActions.get(`${BASE_URL}/mailOutboundConfigs`);
  if (res.ok) {
    return res.json();
  }
  return [];
};

const patchMailOutboundConfig = async (
  id: number,
  patch: Partial<MailOutBoundConfigDTO>,
): Promise<MailOutBoundConfigDTO | null> => {
  const res = await ajaxActions.patch(`${BASE_URL}/mailOutboundConfigs/${id}`, patch);
  if (res.ok) {
    return res.json();
  }
  ToastS.generalError();
  return null;
};

const deleteMailOutboundConfig = async (id: number): Promise<boolean> => {
  const res = await ajaxActions.del(`${BASE_URL}/mailOutboundConfigs/${id}`);
  if (res.ok) {
    ToastS.info('outbound-config-removed', 'E-Mail-Konfiguration gelöscht.');
    return true;
  }
  ToastS.generalError();
  return false;
};

const sendTestMail = async (id: number): Promise<boolean> => {
  const res = await ajaxActions.get(`${BASE_URL}/mailOutboundConfigs/${id}/testMail`);
  if (res.ok) {
    ToastS.success('send.mail', 'Test-E-Mail erfolgreich versendet');
    MixpanelS.track(ConstantS.TrackingEvents.MailAccountConnected);
    return true;
  }
  ToastS.error('send.mail', 'Überprüfe Deine E-Mail-Konfiguration');
  return false;
};

const resolveHint = (provider: string) =>
  DataS.mailProviderHints.filter((h) => h.provider === provider).length === 1
    ? DataS.mailProviderHints.filter((h) => h.provider === provider)[0]
    : undefined;

const resolveProviderConfig = (provider: string) => {
  const config: Partial<MailOutBoundConfigDTO> = { auth: true, replyTo: true };

  if (provider === 'gmail') {
    config.smptHost = 'smtp.gmail.com';
    config.smptPort = 587;
    config.startTls = true;
  } else if (provider === 'gmx') {
    config.smptHost = 'mail.gmx.net';
    config.smptPort = 587;
    config.startTls = true;
  } else if (provider === 'strato') {
    config.smptHost = 'smtp.strato.de';
    config.smptPort = 465;
    config.startTls = false;
  } else if (provider === 'web-de') {
    config.smptHost = 'smtp.web.de';
    config.smptPort = 587;
    config.startTls = true;
  } else if (provider === 'office365') {
    config.smptHost = 'smtp.office365.com';
    config.smptPort = 587;
    config.startTls = true;
  } else if (provider === 'outlook' || provider === 'hotmail') {
    config.smptHost = 'smtp-mail.outlook.com';
    config.smptPort = 587;
    config.startTls = true;
  } else if (provider === 'djbaukasten') {
    config.smptHost = 'simdesign.de';
    config.smptPort = 465;
    config.startTls = false;
  } else if (provider === 't-online') {
    config.smptHost = 'securesmtp.t-online.de';
    config.smptPort = 465;
    config.startTls = false;
  } else if (provider === '1und1') {
    config.smptHost = 'smtp.1und1.de';
    config.smptPort = 587;
    config.startTls = true;
  } else if (provider === 'yahoo') {
    config.smptHost = 'smtp.mail.yahoo.com';
    config.smptPort = 587;
    config.startTls = true;
    config.replyTo = false;
  } else if (provider === 'jimdo') {
    config.smptHost = 'secure.emailsrvr.com';
    config.smptPort = 465;
    config.startTls = false;
  } else if (provider === '1blu') {
    config.smptHost = 'smtp.1blu.de';
    config.smptPort = 587;
    config.startTls = true;
  } else if (provider === 'united-domains') {
    config.smptHost = 'smtps.udag.de';
    config.smptPort = 465;
    config.startTls = false;
  } else if (provider === 'hetzner') {
    config.smptHost = 'mail.your-server.de';
    config.smptPort = 587;
    config.startTls = true;
  } else if (provider === 'mail-de') {
    config.smptHost = 'smtp.mail.de';
    config.smptPort = 587;
    config.startTls = true;
  } else if (provider === 'icloud') {
    config.smptHost = 'smtp.mail.me.com';
    config.smptPort = 587;
    config.startTls = true;
  } else if (provider === 'freenet') {
    config.smptHost = 'mx.freenet.de';
    config.smptPort = 587;
    config.startTls = true;
  } else if (provider === 'goneo') {
    config.smptHost = 'smtp.goneo.de';
    config.smptPort = 465;
    config.startTls = false;
  } else if (provider === 'webador') {
    config.smptHost = 'mail.webador.com';
    config.smptPort = 587;
    config.startTls = true;
  } else if (provider === 'one-com') {
    config.smptHost = 'send.one.com';
    config.smptPort = 465;
    config.startTls = false;
  } else if (provider === 'ovh') {
    config.smptHost = 'ssl0.ovh.net';
    config.smptPort = 587;
    config.startTls = true;
  } else {
    return null; // Return null if the provider is not found
  }
  return config;
};

const resolveSmtpConfig = (emailAddress: string) => {
  const config: Partial<MailOutBoundConfigDTO> = { auth: true, replyTo: true };

  if (emailAddress.includes('gmail')) {
    config.smptHost = 'smtp.gmail.com';
    config.smptPort = 587;
    config.startTls = true;
    config.provider = 'gmail';
  } else if (emailAddress.includes('gmx')) {
    config.smptHost = 'mail.gmx.net';
    config.smptPort = 587;
    config.startTls = true;
    config.provider = 'gmx';
  } else if (emailAddress.includes('strato')) {
    config.smptHost = 'smtp.strato.de';
    config.smptPort = 465;
    config.startTls = false;
    config.provider = 'strato';
  } else if (emailAddress.includes('web.de')) {
    config.smptHost = 'smtp.web.de';
    config.smptPort = 587;
    config.startTls = true;
    config.provider = 'web-de';
  } else if (emailAddress.includes('office365')) {
    config.smptHost = 'smtp.office365.com';
    config.smptPort = 587;
    config.startTls = true;
    config.provider = 'office365';
  } else if (emailAddress.includes('outlook') || emailAddress.includes('hotmail')) {
    config.smptHost = 'smtp-mail.outlook.com';
    config.smptPort = 587;
    config.startTls = true;
    config.provider = 'outlook';
  } else if (emailAddress.includes('djbaukasten')) {
    config.smptHost = 'simdesign.de';
    config.smptPort = 465;
    config.startTls = false;
    config.provider = 'djbaukasten';
  } else if (emailAddress.includes('t-online')) {
    config.smptHost = 'securesmtp.t-online.de';
    config.smptPort = 465;
    config.startTls = false;
    config.provider = 't-online';
  } else if (emailAddress.includes('1und1')) {
    config.smptHost = 'smtp.1und1.de';
    config.smptPort = 587;
    config.startTls = true;
    config.provider = '1und1';
  } else if (emailAddress.includes('yahoo')) {
    config.smptHost = 'smtp.mail.yahoo.com';
    config.smptPort = 587;
    config.startTls = true;
    config.provider = 'yahoo';
    config.replyTo = false;
  } else if (emailAddress.includes('jimdo')) {
    config.smptHost = 'secure.emailsrvr.com';
    config.smptPort = 465;
    config.startTls = false;
    config.provider = 'jimdo';
  } else if (emailAddress.includes('1blu')) {
    config.smptHost = 'smtp.1blu.de';
    config.smptPort = 587;
    config.startTls = true;
    config.provider = '1blu';
  } else if (emailAddress.includes('united-domains')) {
    config.smptHost = 'smtps.udag.de';
    config.smptPort = 465;
    config.startTls = false;
    config.provider = 'united-domains';
  } else if (emailAddress.includes('hetzner')) {
    config.smptHost = 'mail.your-server.de';
    config.smptPort = 587;
    config.startTls = true;
    config.provider = 'hetzner';
  } else if (emailAddress.includes('mail.de')) {
    config.smptHost = 'smtp.mail.de';
    config.smptPort = 587;
    config.startTls = true;
    config.provider = 'mail-de';
  } else if (emailAddress.includes('icloud')) {
    config.smptHost = 'smtp.mail.me.com';
    config.smptPort = 587;
    config.startTls = true;
    config.provider = 'icloud';
  } else if (emailAddress.includes('freenet')) {
    config.smptHost = 'mx.freenet.de';
    config.smptPort = 587;
    config.startTls = true;
    config.provider = 'freenet';
  } else if (emailAddress.includes('goneo')) {
    config.smptHost = 'smtp.goneo.de';
    config.smptPort = 465;
    config.startTls = false;
    config.provider = 'goneo';
  } else if (emailAddress.includes('webador')) {
    config.smptHost = 'mail.webador.com';
    config.smptPort = 587;
    config.startTls = true;
    config.provider = 'webador';
  } else if (emailAddress.includes('one.com')) {
    config.smptHost = 'send.one.com';
    config.smptPort = 465;
    config.startTls = false;
    config.provider = 'one.com';
  } else if (emailAddress.includes('ovh')) {
    config.smptHost = 'ssl0.ovh.net';
    config.smptPort = 587;
    config.startTls = true;
    config.provider = 'ovh';
  } else {
    config.smptHost = undefined;
    config.smptPort = undefined;
    config.startTls = true;
    config.provider = 'other';
  }
  config.login = emailAddress;
  return config;
};

const testMailAvailable = (mailConfig: MailOutBoundConfigDTO) => {
  const { externalAccountId, login, password, smptHost, smptPort } = mailConfig;
  if (externalAccountId) {
    return true;
  }
  return isDefined(login) && isDefined(password) && isDefined(smptHost) && smptPort !== null;
};

const getDomain = async (configId: number): Promise<MailgunDomainDTO | null> => {
  const res = await ajaxActions.get(`${BASE_URL}/mailOutboundConfigs/${configId}/domain`);
  if (res.ok) {
    return res.json();
  }
  return null;
};

const verifyDomain = async (configId: number): Promise<MailgunDomainDTO | null> => {
  const res = await ajaxActions.get(`${BASE_URL}/mailOutboundConfigs/${configId}/verify-domain`);
  if (res.ok) {
    const domain: MailgunDomainDTO = await res.json();
    if (domain.verified) {
      MixpanelS.track(ConstantS.TrackingEvents.MailgunDomainVerified);
      ToastS.success('domain-verified', 'Domain verifiziert.');
    }
    return domain;
  }
  return null;
};

const unsupportedProviders = [
  'gmail.com',
  'yahoo.com',
  'outlook.com',
  't-online.de',
  'web.de',
  'me.com',
  'icloud.com',
  'gmx.de',
  'gmx.net',
];

const isSmtpRelayAvailable = (emailAddress: string) => {
  const domain = emailAddress.split('@').pop()?.toLowerCase();
  return domain ? !unsupportedProviders.includes(domain) : false;
};

const resolveConfigType = (emailAddress: string): MailConfigType => {
  if (emailAddress.includes('office365') || emailAddress.includes('outlook')) {
    return 'AZURE';
  }
  if (emailAddress.includes('gmail') || emailAddress.includes('bookitup')) {
    return 'GOOGLE';
  }
  return 'SMTP';
};

const isSubmittable = (config: Partial<MailOutBoundConfigDTO>, type?: MailConfigType) => {
  if (type === 'SMTP') {
    const { smptHost, smptPort, login, password } = config;
    return isDefined(smptHost) && smptPort !== undefined && isDefined(login) && isDefined(password);
  }
  return true;
};

const getParticipantAddress = (address: IdPersonDescriptionEmailDto) => {
  const { personDescription, email } = address;
  if (isDefined(personDescription)) {
    return `${personDescription} <${email}>`;
  }
  return email;
};

const resendMessage = async (reportId: string): Promise<boolean> => {
  const res = await ajaxActions.get(`${BASE_URL}/mailgun/resend?reportId=${reportId}`);
  if (res.ok) {
    return true;
  }
  ToastS.generalError();
  return false;
};

const getFailureReportDescription = async (reportId: string): Promise<MailgunDeliveryFailureReportDTO | null> => {
  const res = await ajaxActions.get(`${BASE_URL}/mailgun/failure-report/${reportId}`);
  if (res.ok) {
    return res.json();
  }
  return null;
};

const getDefaultFolderIMAP = async (configId: number): Promise<MailFolderDto | null> => {
  const res = await ajaxActions.get(`${BASE_URL}/imap/default-folder?configId=${configId}`);
  if (res.ok) {
    return res.json();
  }
  return null;
};

const getMessagesIMAP = async (filter: MessagesFilterIMAP): Promise<PageWrapper | null> => {
  const params = BrowserS.toSearchParams(filter);
  const res = await ajaxActions.get(`${BASE_URL}/imap/messages?${params.toString()}`);
  if (res.ok) {
    return res.json();
  }
  return null;
};

const getMessageIMAP = async (
  messageId: number | string,
  configId: number,
  folderName: string,
): Promise<MessageDTO | null> => {
  const params = new URLSearchParams({ configId, folderName } as any);
  const res = await ajaxActions.get(`${BASE_URL}/imap/messages/${messageId}?${params.toString()}`);
  if (res.ok) {
    return res.json();
  }
  return null;
};

const getMessageAttachmentIMAP = async (
  messageId: number | string,
  configId: number,
  folderName: string,
  attachmentId: string,
): Promise<Response | null> => {
  const params = new URLSearchParams({
    configId: `${configId}`,
    folderName,
    attachmentId,
  });
  const res = await ajaxActions.get(`${BASE_URL}/imap/messages/${messageId}/attachments?${params.toString()}`, {
    headers: { Accept: '*/*' },
  });
  if (res.ok) {
    return res;
  }
  return null;
};

const changeMessageSeenFlagIMAP = async (
  messageId: number | string,
  configId: number,
  folderName: string,
  value: boolean,
): Promise<boolean> => {
  const params = new URLSearchParams({ configId, folderName, value } as any);
  const res = await ajaxActions.patch(`${BASE_URL}/imap/messages/${messageId}/seen?${params.toString()}`, null);
  if (res.ok) {
    ToastS.info(`seen-flag-changed-${messageId}`, `Nachricht wurde als ${value ? 'gelesen' : 'ungelesen'} markiert.`);
    return true;
  }
  ToastS.generalError();
  return false;
};

const getFolderCountDescription = (folder: EmailFolderInfo) => {
  const { totalMessages, unreadMessages } = folder;
  if (totalMessages === undefined) return null;
  if (totalMessages === 0) return 'Keine Nachrichten';
  let str = `${folder.totalMessages} nachrichten`;
  if (unreadMessages) {
    str += `, ${folder.unreadMessages} ungelesen`;
  }
  return str;
};

const getRecipientDescription = (obj: Partial<IdPersonDescriptionEmailDto>) => {
  const { personDescription, email } = obj;
  if (personDescription) return `${personDescription}<${email}>`;
  return email;
};

const getInitials = (person: Partial<IdPersonDescriptionEmailDto>) => {
  const { personDescription, email } = person;
  if (personDescription) {
    const tmp = personDescription.split(' ');
    if (tmp.length === 2) {
      return `${tmp[0][0]}${tmp[1][0]}`.toUpperCase();
    }
    return personDescription[0].toUpperCase();
  }
  if (email) {
    return email[0].toUpperCase();
  }
  return null;
};

const getFolderLabel = (name: string) => {
  if (name === 'INBOX' || name === 'inbox') return 'Posteingang';
  if (name === 'drafts') return 'Entwürfe';
  if (name === 'scheduled') return 'Geplant';
  if (name === 'outbound') return 'Gesendet';
  if (name === 'failed') return 'Fehlgeschlagen';
  if (name === 'trash') return 'Papierkorb';
  return name;
};

// eslint-disable-next-line import/prefer-default-export
export const MailS = {
  create,
  fetchMailOutboundConfig,
  fetchMailOutboundConfigs,
  deleteMailOutboundConfig,
  patchMailOutboundConfig,
  sendTestMail,
  getDomain,
  verifyDomain,
  resolveHint,
  resolveProviderConfig,
  resolveSmtpConfig,
  testMailAvailable,
  isSmtpRelayAvailable,
  resolveConfigType,
  isSubmittable,
  getParticipantAddress,
  getFailureReportDescription,
  resendMessage,
  getDefaultFolderIMAP,
  getMessagesIMAP,
  getMessageIMAP,
  getMessageAttachmentIMAP,
  changeMessageSeenFlagIMAP,
  getFolderCountDescription,
  getRecipientDescription,
  getFolderLabel,
  getInitials,
};
